import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useQuietPeriod,
  useUpdateQuietPeriod,
} from "../../hooks/useQuietPeriods";
import AuditInfo from "../../components/ui/AuditInfo";
import { auditApi } from "../../services/api";
import { QuietPeriodUpdate } from "../../types/quiet_period";
import { Input, Textarea } from "../../components/ui/Input";

const EditQuietPeriodPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const quietPeriodId = Number(id);
  const navigate = useNavigate();
  const { data: qp, isLoading } = useQuietPeriod(quietPeriodId);
  const updateMutation = useUpdateQuietPeriod();
  const [form, setForm] = useState<QuietPeriodUpdate>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (qp) {
      setForm({
        name: qp.name,
        description: qp.description || "",
        from: qp.from ? qp.from.slice(0, 16) : "",
        to: qp.to ? qp.to.slice(0, 16) : "",
        tags: qp.tags || [],
      });
    }
  }, [qp]);

  const handleChange = (field: keyof QuietPeriodUpdate, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: QuietPeriodUpdate = {
        name: form.name?.trim() || undefined,
        description: form.description || undefined,
        from: form.from,
        to: form.to,
        tags: (form.tags || [])
          .map((t) => (t || "").toString().trim())
          .filter(Boolean),
      };
      const updated = await updateMutation.mutateAsync({
        id: quietPeriodId,
        data: payload,
      });
      navigate(`/quiet-periods/${updated.id}`);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !qp) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  const tagsInput = (form.tags || []).join(", ");

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Edit Dynamic Date: {qp.name}
        </h2>
        <p className="text-sm text-gray-500">
          Update dynamic date configuration
        </p>
      </div>

      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <Input
          label="Name"
          type="text"
          value={form.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          variant="rounded-md"
        />
        <Textarea
          label="Description"
          rows={3}
          value={form.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          variant="rounded-md"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="From"
            type="datetime-local"
            value={(form.from as string) || ""}
            onChange={(e) => handleChange("from", e.target.value)}
            variant="rounded-md"
          />
          <Input
            label="To"
            type="datetime-local"
            value={(form.to as string) || ""}
            onChange={(e) => handleChange("to", e.target.value)}
            variant="rounded-md"
          />
        </div>

        <Input
          label="Tags"
          type="text"
          placeholder="e.g. holiday, maintenance"
          value={tagsInput}
          onChange={(e) =>
            handleChange(
              "tags",
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          variant="rounded-md"
          helperText="Comma-separated list. Used by transitions and date pickers to exclude dates."
        />

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate(`/quiet-periods/${quietPeriodId}`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      {qp?.id ? (
        <AuditInfo
          resource="quiet-periods"
          id={qp.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
};

export default EditQuietPeriodPage;
